// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import loadNewsList from "@conklin/App/Handlers/NewsList/LoadNewsList";
import updateNewsListParameter from "@conklin/App/Handlers/NewsList/UpdateNewsListParameter";
import { CustomAppState } from "@conklin/App/Store/Reducers";
import { getNewsList } from "@conklin/App/Store/Selectors/NewsListSelectors";
import Button, { ButtonPresentationProps } from "@conklin/MobiusOverrides/Button/Button";
import LazyImage, { LazyImageProps } from "@conklin/MobiusOverrides/LazyImage";
import Pagination, { PaginationPresentationProps } from "@conklin/MobiusOverrides/Pagination";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Drawer, { DrawerPresentationProps } from "@insite/mobius/Drawer";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import convertToNumberIfString from "@insite/mobius/utilities/convertToNumberIfString";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import truncate from "lodash/truncate";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    defaultPageSize = "defaultPageSize",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.defaultPageSize]: number;
    };
}

// Xng-Changes: using custom api
const mapStateToProps = (state: CustomAppState) => {
    const parameter = state.xNewsList.parameter;
    const pages = getNewsList(state, parameter);
    return {
        location: getLocation(state),
        pages,
        parameter,
        isTabletScreen: state.xCommon.isTabletScreen,
        categoriesItems: state.xNewsList.categories,
    };
};

// Xng-Changes: using custom api
const mapDispatchToProps = {
    loadNewsList,
    updateNewsListParameter,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export interface NewsListViewStyles {
    titleText?: TypographyProps;
    authorText?: TypographyProps;
    publishDateText?: TypographyProps;
    listWrapper?: InjectableCss;
    newsWrapper?: InjectableCss;
    summaryWrapper?: InjectableCss;
    dateAndAuthorWrapper?: InjectableCss;
    readMoreLink?: LinkPresentationProps;
    pagination?: PaginationPresentationProps;
    // Xng-Changes
    listContainer?: InjectableCss;
    content?: InjectableCss;
    drawerWrapper?: InjectableCss;
    doneBtn?: ButtonPresentationProps;
    drawer?: DrawerPresentationProps;
    drawerTitle?: TypographyPresentationProps;
    drawerTitleWrapper?: InjectableCss;
    paginationWrapper?: InjectableCss;
    paginationWithFiltersWrapper?: InjectableCss;
    closeBtn?: LinkPresentationProps;
    mobileFiltersLink?: LinkPresentationProps;

    wrapper?: InjectableCss;
    imageClickableWrapper?: ClickablePresentationProps;
    image?: LazyImageProps;
    cardContentWrapper?: InjectableCss;
    title?: LinkPresentationProps;
    subtitle?: LinkPresentationProps;
    textContent?: TypographyPresentationProps;
    smallLink?: LinkPresentationProps;
    link?: LinkPresentationProps;
    buttonWrapper?: InjectableCss;
}

export const newsListViewStyles: NewsListViewStyles = {
    titleText: {
        size: 20,
        css: css`
            margin-bottom: 0;
        `,
    },
    newsWrapper: {
        css: css`
            margin-bottom: 15px;
            border-top: 1px solid ${getColor("common.border")};
            padding-top: 15px;
        `,
    },
    pagination: {
        cssOverrides: {
            pagination: css`
                border-top: 1px solid ${getColor("common.border")};
                padding-top: 15px;
            `,
        },
    },
    summaryWrapper: {
        css: css`
            margin-top: 8px;
        `,
    },
    authorText: {
        css: css`
            font-weight: bold;
            margin-right: 8px;
        `,
    },
    publishDateText: {
        css: css`
            font-style: italic;
        `,
    },
};

const styles = newsListViewStyles;

// XNG-Changes: adding state
interface State {
    filterDrawerIsOpen: boolean;
}

class NewsListView extends React.Component<Props, State> {
    // XNG-Changes: adding state
    constructor(props: Props) {
        super(props);

        this.state = {
            filterDrawerIsOpen: false,
        };
    }

    UNSAFE_componentWillMount() {
        if (!this.props.pages.value && !this.props.pages.isLoading) {
            // this.props.loadPagesForParent(this.props.parameter);
            // XNG-Changes: custom api
            this.props.loadNewsList({ ...this.props.parameter });
        }
        this.checkQueryString();
    }

    componentDidUpdate() {
        if (!this.props.pages.value && !this.props.pages.isLoading) {
            // this.props.loadPagesForParent(this.props.parameter);
            // XNG-Changes: custom api
            this.props.loadNewsList({ ...this.props.parameter });
        }
        this.checkQueryString();
    }

    changePage = (newPageIndex: number) => {
        this.updateQueryString(newPageIndex, this.props.parameter.pageSize);
    };

    changeResultsPerPage = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newPageSize = parseInt(event.currentTarget.value, 10);
        this.updateQueryString(1, newPageSize);
    };

    updateQueryString(page: number, pageSize?: number) {
        const {
            history,
            location: { pathname },
            parameter: { categories },
        } = this.props;
        let newLocation = `${pathname}?page=${page}`;
        if (pageSize) {
            newLocation += `&pageSize=${pageSize}`;
        }
        if (categories) {
            newLocation += `&categories=${categories}`;
        }
        history.push(newLocation);
    }

    checkQueryString() {
        const {
            location: { search },
            parameter,
            pages: { isLoading },
        } = this.props;
        const parsedQueryString = parseQueryString<{ page?: string; pageSize?: string; categories?: string }>(search);
        const newPage = parsedQueryString.page ? convertToNumberIfString(parsedQueryString.page) : undefined;
        // const newPageSize = parsedQueryString.pageSize
        //     ? convertToNumberIfString(parsedQueryString.pageSize)
        //     : undefined;
        const newCategories = parsedQueryString.categories ? parsedQueryString.categories : undefined;

        if (
            !isLoading &&
            (newPage !== parameter?.page ||
                // newPageSize !== parameter?.pageSize ||
                newCategories !== parameter.categories)
        ) {
            // XNG-changes: using custom api
            this.props.updateNewsListParameter({
                parameter: {
                    page: newPage,
                    pageSize: this.props.fields.defaultPageSize,
                    categories: newCategories,
                },
            });
        }
    }

    // <XNG-Changes>: adding drawer for tablet / mobile filters
    handleOpenFilterDrawer() {
        this.setState({ filterDrawerIsOpen: true });
    }

    handleCloseFilterDrawer() {
        this.setState({ filterDrawerIsOpen: false });
    }
    // </XNG-Changes>

    render() {
        const {
            // links,
            pages: { value: pages, isLoading },
            fields: { defaultPageSize },
            parameter,
        } = this.props;

        if (!pages || !pages.length) {
            return null;
        }

        // XNG-Changes
        const selectedCategories = parameter?.categories?.split(",");
        const totalItems =
            selectedCategories?.reduce(
                (sum, category) =>
                    sum + (this.props.categoriesItems?.find(cat => cat.categoryName === category)?.count || 0),
                0,
            ) || (this.props.pages as any).pagination?.totalItemCount;
        const page = parameter?.page ?? 1;
        const pageSize = parameter.pageSize || 12;

        // const sortedAndPagedPages = sortBy(pages, o => o.fields["publishDate"])
        //     .reverse()
        //     .slice((page - 1) * pageSize, page * pageSize);

        // XNG-Changes
        const categories: any = (this.props.pages as any)?.properties?.categories || {};

        // XNG-Changes
        const pagination = (
            <Pagination
                resultsCount={totalItems}
                currentPage={page}
                resultsPerPage={pageSize}
                resultsPerPageOptions={[defaultPageSize, defaultPageSize * 2, defaultPageSize * 3, defaultPageSize * 4]}
                onChangePage={this.changePage}
                onChangeResultsPerPage={this.changeResultsPerPage}
                pageSizeCookie="NewsList-PageSize"
                {...styles.pagination}
                showResultsPerPage={false}
            />
        );
        return (
            <StyledWrapper {...styles.listWrapper}>
                {/* Xng-Changes: add another pagination above the widget */}
                {(totalItems > defaultPageSize || this.props.isTabletScreen) && (
                    <StyledWrapper {...styles.paginationWithFiltersWrapper}>
                        {/* Xng-Changes: adding drawer for tablet / mobile filters */}
                        {!categories.length && (
                            <Link
                                aria-label="open filter drawer"
                                data-test-selector="filter-drawer-trigger"
                                {...styles.mobileFiltersLink}
                                onClick={() => this.handleOpenFilterDrawer()}
                            >
                                {translate("Filter Results")}
                            </Link>
                        )}
                        {totalItems > defaultPageSize && pagination}
                    </StyledWrapper>
                )}
                <LoadingOverlay loading={isLoading}>
                    <StyledWrapper {...styles.listContainer}>
                        {/* XNG-Changes: restructure + make image clickable */}
                        {pages.map(page => (
                            <StyledWrapper key={page.id} {...styles.content}>
                                <StyledWrapper {...styles.wrapper}>
                                    <Clickable
                                        href={page.url}
                                        {...styles.imageClickableWrapper}
                                        {...{ imagePosition: "column" }}
                                    >
                                        <LazyImage
                                            src={page.cardImage}
                                            {...styles.image}
                                            altText={page.title || "Conklin Card Image"}
                                        />
                                    </Clickable>
                                    <StyledWrapper {...styles.cardContentWrapper}>
                                        <Link href={page.url} {...styles.subtitle} aria-label="subtitle">
                                            {page.subtitle}
                                        </Link>
                                        <Link href={page.url} {...styles.title}>
                                            {page.title}
                                        </Link>
                                        <Typography {...styles.textContent}>
                                            {parse(truncate(page.cardSummary, { length: 500 }), parserOptions)}
                                        </Typography>
                                        {page.url && (
                                            <StyledWrapper {...styles.buttonWrapper}>
                                                <Link
                                                    href={page.url}
                                                    {...styles.smallLink}
                                                    aria-label="read more button"
                                                >
                                                    {translate("Read More")}
                                                </Link>
                                            </StyledWrapper>
                                        )}
                                    </StyledWrapper>
                                </StyledWrapper>
                            </StyledWrapper>
                        ))}
                    </StyledWrapper>
                </LoadingOverlay>
                {/* Xng-Changes: adding a wrapper for styles */}
                {totalItems > defaultPageSize && (
                    <StyledWrapper {...styles.paginationWrapper}>{pagination}</StyledWrapper>
                )}
                {/* Xng-Changes: adding drawer for tablet / mobile filters */}
                <Drawer
                    isOpen={this.state.filterDrawerIsOpen}
                    handleClose={() => this.handleCloseFilterDrawer()}
                    position="left"
                    draggable={false}
                    contentLabel="filter drawer"
                    headline={
                        <StyledWrapper {...styles.drawerTitleWrapper}>
                            <Typography {...styles.drawerTitle}>{translate("Refine Results")}</Typography>
                            <Link {...styles.closeBtn} onClick={() => this.handleCloseFilterDrawer()}></Link>
                        </StyledWrapper>
                    }
                    {...styles.drawer}
                >
                    <StyledWrapper {...styles.drawerWrapper}>
                        {createWidgetElement("NewsList/NewsListFilter", {
                            fields: { accordionExpanded: true, showMoreExpanded: true },
                        })}
                        <Button {...styles.doneBtn} onClick={() => this.handleCloseFilterDrawer()} aria-label="Done">
                            {translate("Done")}
                        </Button>
                    </StyledWrapper>
                </Drawer>
            </StyledWrapper>
        );
    }
}

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(NewsListView)),
    definition: {
        group: "News List",
        allowedContexts: ["NewsListPage"],
        fieldDefinitions: [
            {
                name: fields.defaultPageSize,
                editorTemplate: "IntegerField",
                // Xng-Changes
                defaultValue: 12,
                fieldType: "General",
                min: 1,
                max: 20,
            },
        ],
    },
};

export default widgetModule;
